import {
  bandValue,
  bandInterval
} from '@aytm/app/insights/dashboard/components/ui/SplitChart/AttributeChart/AttributeChart'
import { GetCellData, GetColumnData } from '@aytm/app/insights/dashboard/helpers/values'
import { truncateStringToLength } from '@aytm/app/insights/dashboard/lib/strings'
import type {
  DataItemValue,
  ItemType,
  Metrics,
  StatItemValue
} from '@aytm/app/insights/dashboard/models/types.ClientDTO'
import math from '@aytm/app/stats/lib/util/math'

import type { PlotLineOrBand, SeriesOptionsType } from 'highcharts'

type PlotLineOrBandWithOptions = Highcharts.PlotLineOrBand & { options: { from: number; to: number } }

type WithRender<T> = T & { render: () => void }

type SeriesDataItem = {
  uid: string
  name: string
  value: number
  low: number
  high: number
  from: number
  to: number
  sigtest: StatItemValue['sigtest']
  type: DataItemValue['type']
  suffix: string
}

interface ChartDataPoint extends Highcharts.Point {
  metadata: SeriesDataItem
}

export type ComposeSeriesParams = {
  items: ItemType[]
  sortByAttribute: string
  metrics: Metrics
  defaultMetrics: Metrics
  confidenceLevel: number
  getColumnData: GetColumnData
  getCellData: GetCellData
}

export function composeSeriesForChart({
  items,
  sortByAttribute,
  metrics,
  defaultMetrics,
  confidenceLevel,
  getColumnData,
  getCellData
}: ComposeSeriesParams): Highcharts.SeriesOptionsType[] {
  const result: Highcharts.SeriesOptionsType[] = items
    .map(item => {
      const uid = item.uid
      const { text } = getColumnData(item)
      const { hasData, value, valueType, sampleSize, sigtest } = getCellData(
        item,
        sortByAttribute,
        metrics,
        defaultMetrics
      )
      return { uid, text, hasData, value, valueType, sampleSize, sigtest }
    })
    .filter(({ hasData }) => hasData)
    .map(({ uid, text, value, valueType, sampleSize, sigtest }) => {
      const freshValue = (value ?? 0) as number
      const roundedValue = Math.round(100 * freshValue) / 100
      const [errLo, errHi] = math.calcErrorForNode((freshValue * sampleSize) / 100, sampleSize, confidenceLevel * 100)
      const [low, high] = [
        sampleSize > 0 ? (errLo * 100) / sampleSize : 0,
        sampleSize > 0 ? (errHi * 100) / sampleSize : 0
      ]
      const [from, to] = [freshValue - low, freshValue + high]
      return {
        uid,
        name: truncateStringToLength(text, 26, '...'),
        value: roundedValue,
        low,
        high,
        from,
        to,
        sigtest,
        type: valueType,
        suffix: 'percent' === valueType ? '%' : ''
      } //as SeriesDataItem
    })
    .reduce((acc, cur) => {
      acc.push({
        type: 'column',
        name: cur.name,
        data: [{ y: cur.value, metadata: cur } as ChartDataPoint],
        zIndex: 11,
        events: {
          mouseOver: makeMouseOverHandler(confidenceLevel),
          mouseOut: handleMouseOut
        }
      })
      return acc
    }, [] as Highcharts.SeriesOptionsType[])

  if (result.length) {
    // Add series for confidence interval
    result.push({
      type: 'column',
      color: '#cdede5',
      showInLegend: false
    })
    // Add series for "selected" data
    result.push({
      type: 'column',
      showInLegend: false
    })
  }

  return result
}

function makeMouseOverHandler(confidenceLevel: number) {
  return function handleMouseOver(this: Highcharts.Series) {
    const metadata = (this.data[0] as ChartDataPoint)?.metadata
    if (!metadata) {
      return
    }
    const colorIndex = (this as Highcharts.PlotSeriesOptions).colorIndex
    // Update legend
    this.chart.legend.options.symbolRadius = 0
    // this.chart.legend.options.layout = 'vertical'

    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      ;(this.chart.legend as WithRender<Highcharts.Legend>).render()
    } catch {
      this.chart.legend.update({}, true)
    }

    // Update series
    const last = this.chart.series.length - 1

    this.chart.series.forEach(function (ser, idx) {
      const uid = (ser.data[0] as ChartDataPoint)?.metadata?.uid
      if (last === idx) {
        // Show "selected" series in legend
        ser.update(
          {
            showInLegend: true,
            name: bandValue(confidenceLevel),
            color: `url(#hatch-${colorIndex})`
          } as SeriesOptionsType,
          false
        )
      } else if (last - 1 === idx) {
        // Show "band" series in legend
        ser.update(
          {
            showInLegend: true,
            name: bandInterval(metadata.low, metadata.high)
          } as SeriesOptionsType,
          false
        )
      } else if (!metadata?.sigtest?.some?.(v => uid === v[0])) {
        // Change color for "selected" series and hide then from legend
        ser.update(
          {
            color: `url(#hatch-${colorIndex})`,
            showInLegend: false
          } as SeriesOptionsType,
          false
        )
      } else {
        // Hide other series from legend
        ser.update({ showInLegend: false } as SeriesOptionsType, false)
      }
    })
    // Redraw chart
    this.chart.redraw()
    // Plot value line
    this.chart.yAxis[0].addPlotLine({
      value: metadata.value,
      color: '#29abe2',
      width: 1,
      zIndex: 2,
      dashStyle: 'Dash',
      id: 'value'
    })
    // Show confidence interval band
    const band = this.chart.yAxis[0].addPlotBand({
      from: metadata.value,
      to: metadata.value,
      color: '#cdede5',
      id: 'interval'
    })

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    Object.assign(band as PlotLineOrBandWithOptions, { options: (band as PlotLineOrBandWithOptions).options || {} })
    Object.assign((band as PlotLineOrBandWithOptions).options, { from: metadata.from, to: metadata.to })
    ;(band as WithRender<PlotLineOrBand>).render()
  }
}

function handleMouseOut(this: Highcharts.Series) {
  // Update legend
  this.chart.legend.options.symbolRadius = 8
  this.chart.legend.options.layout = 'horizontal'
  ;(this.chart.legend as WithRender<Highcharts.Legend>).render()
  // Update series
  const last = this.chart.series.length - 1
  this.chart.series.forEach(function (ser, idx) {
    const name = (ser.data[0] as ChartDataPoint)?.metadata?.name || ser.name
    // Hide "selected" and "band" series from legend
    if (last === idx || last - 1 === idx) ser.update({ showInLegend: false } as SeriesOptionsType, false)
    // Show in legend and set default color for other series
    else ser.update({ color: undefined, showInLegend: true, name } as SeriesOptionsType, false)
  })
  // Redraw chart
  this.chart.redraw()
  // Delete value line
  this.chart.yAxis[0].removePlotBand('interval')
  // Delete confidence interval band
  this.chart.yAxis[0].removePlotLine('value')
}
